// anchorMarkers — publishes the active environment's anchors into
// `window.__NAV_MARKERS__` (what moveToActor resolves names against) and offers
// them to the LLM as `{Goto:name}` candidates.
//
// It follows environmentAnchorStore rather than reading the row, so there is one
// reader (`readAnchors`) and a live-placed anchor is walkable at once.

import { environmentAnchorStore } from './environmentAnchorStore.js'

// Replaces __NAV_MARKERS__ with the anchor map, dropping the previous environment's.
// See tests/unit/anchorMarkers.test.mjs.
export function populateNavMarkers(anchors) {
  if (typeof window === 'undefined') return
  // A fresh object, so no stale marker survives a scene switch.
  const reg = {}
  for (const [name, a] of Object.entries(anchors ?? {})) {
    reg[name] = { name, label: a.label ?? '', px: a.px, py: a.py, pz: a.pz }
  }
  window.__NAV_MARKERS__ = reg
}

// The label the LLM reads: the authored one, else the readable name
// ("front_door" → "front door"), since labels are optional.
// See tests/unit/anchorMarkers.test.mjs.
export function promptLabel(anchor) {
  const label = typeof anchor?.label === 'string' ? anchor.label.trim() : ''
  return label || String(anchor?.name ?? '').replace(/_/g, ' ')
}

// An anchor's `[x, y, z]`, or null (console markers don't count).
// See tests/unit/anchorMarkers.test.mjs.
export function anchorPoint(name) {
  if (typeof window === 'undefined' || !name) return null
  const t = window.__NAV_MARKERS__?.[name]
  if (!t || typeof t !== 'object' || Array.isArray(t) || typeof t.name !== 'string') return null
  const p = [t.px, t.py, t.pz]
  return p.every(Number.isFinite) ? p : null
}

// The active environment's anchors. See tests/unit/anchorMarkers.test.mjs.
export function currentEnvironmentAnchors() {
  if (typeof window === 'undefined') return []
  const reg = window.__NAV_MARKERS__
  if (!reg || typeof reg !== 'object') return []
  // Only entries with a string `name`: console markers still resolve for
  // moveToActor but are intentionally never offered to the model.
  return Object.values(reg).filter(t => t && typeof t === 'object' && !Array.isArray(t) && typeof t.name === 'string')
}

// Syncs __NAV_MARKERS__ and the server's list with the anchor store. Returns the
// unsubscribe. See tests/unit/anchorMarkers.test.mjs.
export function bindNavMarkersToAnchorStore(store = environmentAnchorStore) {
  let synced
  const sync = () => {
    const { anchors } = store.getState()
    // Save-state transitions keep the same map; don't re-send.
    if (anchors === synced) return
    synced = anchors
    populateNavMarkers(anchors)
    // Via the window global, not an import: keeps the network layer out of this
    // node:test-loaded module.
    if (typeof window !== 'undefined') window.__CONVORCHER_CLIENT__?.sendEnvironmentContext?.()
  }
  sync()
  return store.subscribe(sync)
}

// Closest anchor on the ground plane as `{ name, distance }`, or null.
// See tests/unit/anchorMarkers.test.mjs.
export function nearestAnchor(point, anchors = currentEnvironmentAnchors()) {
  if (!Array.isArray(point) || !Number.isFinite(point[0]) || !Number.isFinite(point[2])) return null
  let best = null
  for (const a of anchors) {
    if (!Number.isFinite(a?.px) || !Number.isFinite(a?.pz)) continue
    const distance = Math.hypot(a.px - point[0], a.pz - point[2])
    if (!best || distance < best.distance) best = { name: a.name, distance }
  }
  return best
}

// Reports every walk arrival (goto, step, "Walk here") to convorcher, so the LLM
// knows where the avatar is. Returns the unsubscribe.
// See tests/unit/anchorMarkers.test.mjs.
//
// Uses the planned destination: `arrived` is timer-based, and in a hidden tab
// the avatar may still be mid-route. Only a `stopped short` walk reads the live
// position (`getPosition`, injected to keep three.js out of node:test).
export function bindArrivalReports(state, getPosition) {
  let reported = null
  const sync = () => {
    const { phase, requestId, destination, message } = state.getState()
    // `arrived` lasts through the hold beat; report each walk once.
    if (phase !== 'arrived' || requestId === reported) return
    let position
    try {
      position = message !== 'stopped short' && Array.isArray(destination) ? destination : getPosition()
    } catch (err) {
      // NOT marked reported: `arrived` holds for several ticks, so a rig that is
      // momentarily unreadable still reports on the next one. Marking it here
      // lost the walk for good and left the LLM on the previous location.
      console.warn('[anchors] walk arrived but the avatar position is unreadable:', err)
      return
    }
    reported = requestId
    if (typeof window !== 'undefined') window.__CONVORCHER_CLIENT__?.sendAvatarMoved?.(position)
  }
  sync()
  return state.subscribe(sync)
}
